import * as Turbo from "@hotwired/turbo"

// Turbo marks what is loading with aria-busy="true": <html> for a page visit,
// the <form> for a submission, the <turbo-frame> for a frame load. A page
// refresh started during any of them discards the member's pending result.
const TURBO_BUSY_SELECTOR = ":root[aria-busy='true'], form[aria-busy='true'], turbo-frame[aria-busy='true']"

// How long a refresh waits before looking again while Turbo is busy. Checking
// is one selector match, so the interval only bounds how late the refresh runs.
export const REFRESH_RETRY_MS = 250

// A form drops aria-busy as soon as the response headers arrive, but reads the
// body before it starts the redirect visit or renders a 422 in place — a
// refresh in between would overwrite the member's errors. So an HTML response
// counts as in flight until its follow-up starts. The cap only keeps a
// follow-up that never comes (a prevented visit) from holding refreshes off.
// Any frame render clears it, even an unrelated one — accepted, it is rare.
export const SUBMISSION_FOLLOW_UP_MS = 5000
// StreamMessage.contentType, which Turbo does not export. FetchResponse#isHTML
// matches it too, but a stream renders in place with no follow-up to wait for.
const TURBO_STREAM_CONTENT_TYPE = "text/vnd.turbo-stream.html"
let submissionEndedAt = null

document.addEventListener("turbo:submit-end", (event) => {
  const response = event.detail?.fetchResponse
  if (response?.isHTML && !response.contentType?.startsWith(TURBO_STREAM_CONTENT_TYPE)) {
    submissionEndedAt = Date.now()
  }
})
for (const followUp of ["turbo:visit", "turbo:render", "turbo:frame-render"]) {
  document.addEventListener(followUp, () => {
    submissionEndedAt = null
  })
}

function awaitingSubmissionFollowUp() {
  return submissionEndedAt !== null && Date.now() - submissionEndedAt < SUBMISSION_FOLLOW_UP_MS
}

/**
 * True while Turbo has a page visit, a form submission or a frame load in
 * flight.
 *
 * @returns {boolean}
 */
export function turboRequestInFlight() {
  return document.querySelector(TURBO_BUSY_SELECTOR) !== null || awaitingSubmissionFollowUp()
}

/**
 * Re-visits `url` in place — for a poller or a broadcast, never for the
 * member's own click — once Turbo has nothing in flight.
 *
 * Turbo.visit stops the visit and the form submission in flight first
 * (Navigator.startVisit → stop()), so a refresh landing between a click and
 * its response would silently swallow the click. This waits it out instead
 * and, like Turbo's own broadcast refresh (Session.refresh), does not cache a
 * snapshot of the page being replaced.
 *
 * `url` is fixed at the first attempt: Turbo only changes history when the
 * new page renders, and by then this page's controllers have disconnected.
 * Cancelling on disconnect drops the refresh for good — right when the member
 * moved on, and also after a 422 re-render, which keeps their input instead.
 *
 * Known gap: Turbo leaves <html aria-busy> set when a visit is cancelled by a
 * form submission that then renders a 422, so a refresh waits for the next
 * visit. It fails safe — nothing the member did is lost.
 *
 * @param {string} url
 * @returns {() => void} cancels a retry still pending — call it from disconnect()
 */
export function refreshWhenIdle(url) {
  let timer = null

  const attempt = () => {
    if (turboRequestInFlight()) {
      timer = setTimeout(attempt, REFRESH_RETRY_MS)
      return
    }

    timer = null
    Turbo.visit(url, { action: "replace", shouldCacheSnapshot: false })
  }

  attempt()
  return () => clearTimeout(timer)
}
